import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useUIStore } from "./ui";
import apiService from "../services/apiService";
import logService from "../services/logService";

export const useAuthStore = defineStore("auth", () => {
  const uiStore = useUIStore();

  // State
  const user = ref(null);
  const token = ref(null);
  const initialized = ref(false);
  const isAuthenticated = computed(() => !!token.value && !!user.value);

  const userPermissions = computed(() => {
    if (!user.value) return [];

    // Base permissions based on role
    const basePermissions = {
      admin: [
        "read",
        "write",
        "delete",
        "manage_users",
        "manage_content",
        "manage_orders",
        "manage_products",
        "access_admin_panel"
      ],
      client: [
        "read",
        "write_own",
        "manage_profile"
      ],
      developer: [
        "read",
        "write_assigned",
        "manage_profile",
        "access_dev_content",
        "manage_dev_profiles",
        "manage_orders",
        "manage_products",
        "access_admin_panel"
      ],
    };

    let permissions = [...(basePermissions[user.value.role] || [])];

    // Add user-specific permissions
    if (user.value.permissions) {
      if (Array.isArray(user.value.permissions)) {
        // Handle array format (e.g., ["manage_dev_profiles", "manage_orders"])
        user.value.permissions.forEach(pId => {
          if (!permissions.includes(pId)) {
            permissions.push(pId);
          }
        });
      } else if (typeof user.value.permissions === 'object') {
        // Handle object format (e.g., {"manage_dev_profiles": true})
        Object.keys(user.value.permissions).forEach(key => {
          if (user.value.permissions[key] === true && !permissions.includes(key)) {
            permissions.push(key);
          }
        });
      }
    }

    return permissions;
  });

  const hasRole = (role) => {
    return user.value?.role === role;
  };

  // Actions
  const initializeAuth = async () => {
    if (initialized.value) return;

    const storedToken = localStorage.getItem("auth_token");
    const storedUser = localStorage.getItem("auth_user");

    if (storedToken && storedUser) {
      token.value = storedToken;
      try {
        user.value = JSON.parse(storedUser);
        logService.info("Auth initialized from storage", { user: user.value });
      } catch (error) {
        logService.error("Error parsing stored user:", error);
        logout();
      }
    }

    initialized.value = true;
  };

  const login = async (credentials) => {
    try {
      uiStore.setLoading(true);
      logService.info("Attempting login...");

      const response = await apiService.post("/auth/login", credentials);

      if (response.success) {
        token.value = response.data.token;
        user.value = response.data.user;

        // Store in localStorage
        localStorage.setItem("auth_token", response.data.token);
        localStorage.setItem("auth_user", JSON.stringify(response.data.user));

        uiStore.showNotification({
          type: "success",
          title: "Login Successful",
          message: `Welcome back, ${response.data.user.name}!`,
        });

        logService.info("Login successful", { user: response.data.user });
        return { success: true, user: response.data.user };
      } else {
        throw new Error(response.message || "Login failed");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(", ")
        : error.message || "Login failed";

      uiStore.showNotification({
        type: "error",
        title: "Login Failed",
        message: errorMessage,
      });

      logService.error("Login Error:", error);
      return { success: false, error: errorMessage };
    } finally {
      uiStore.setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      uiStore.setLoading(true);
      logService.info("Attempting registration...");

      const response = await apiService.post("/auth/register", userData);

      if (response.success) {
        token.value = response.data.token;
        user.value = response.data.user;

        // Store in localStorage
        localStorage.setItem("auth_token", response.data.token);
        localStorage.setItem("auth_user", JSON.stringify(response.data.user));

        uiStore.showNotification({
          type: "success",
          title: "Registration Successful",
          message: `Welcome to Ruxzom, ${response.data.user.name}!`,
        });

        logService.info("Registration successful", { user: response.data.user });
        return { success: true, user: response.data.user };
      } else {
        throw new Error(response.message || "Registration failed");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(", ")
        : error.message || "Registration failed";

      uiStore.showNotification({
        type: "error",
        title: "Registration Failed",
        message: errorMessage,
      });

      logService.error("Registration Error:", error);
      return { success: false, error: errorMessage };
    } finally {
      uiStore.setLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      uiStore.setLoading(true);
      const response = await apiService.post("/auth/password/forgot", { email });

      if (response.success) {
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return { success: true };
      } else {
        throw new Error(response.message || "Failed to send reset link");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(", ")
        : error.message || "Failed to send reset link";

      uiStore.showNotification({
        type: "error",
        title: "Error",
        message: errorMessage,
      });
      return { success: false, error: errorMessage };
    } finally {
      uiStore.setLoading(false);
    }
  };

  const resetPassword = async (resetData) => {
    try {
      uiStore.setLoading(true);
      const response = await apiService.post("/auth/password/reset", resetData);

      if (response.success) {
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return { success: true };
      } else {
        throw new Error(response.message || "Failed to reset password");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(", ")
        : error.message || "Failed to reset password";

      uiStore.showNotification({
        type: "error",
        title: "Error",
        message: errorMessage,
      });
      return { success: false, error: errorMessage };
    } finally {
      uiStore.setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await apiService.get("/auth/user");

      if (response.success) {
        user.value = response.data;
        localStorage.setItem("auth_user", JSON.stringify(response.data));
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch user");
      }
    } catch (error) {
      logService.error("Fetch User Error:", error);
      return null;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      uiStore.setLoading(true);
      const response = await apiService.put("/auth/profile", profileData);

      if (response.success) {
        user.value = response.data;
        localStorage.setItem("auth_user", JSON.stringify(response.data));

        uiStore.showNotification({
          type: "success",
          title: "Profile Updated",
          message: "Your profile has been updated successfully.",
        });

        return response.data;
      } else {
        throw new Error(response.message || "Failed to update profile");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(", ")
        : error.message || "Failed to update profile";

      uiStore.showNotification({
        type: "error",
        title: "Update Failed",
        message: errorMessage,
      });
      return null;
    } finally {
      uiStore.setLoading(false);
    }
  };

  const logout = async () => {
    try {
      uiStore.setLoading(true);
      logService.info("Logging out...");

      // Call backend API
      await apiService.post("/auth/logout");

      // Clear local storage
      clearAuthData();

      uiStore.showNotification({
        type: "success",
        title: "Logged Out",
        message: "You have been logged out successfully.",
      });

      // Redirect to home page
      if (typeof window !== "undefined") {
        window.location.href = "/";
      }

      return { success: true };
    } catch (error) {
      logService.error("Logout error (proceeding with local cleanup):", error);
      clearAuthData();
      return { success: true };
    } finally {
      uiStore.setLoading(false);
    }
  };

  // Private helper to clear data
  const clearAuthData = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    sessionStorage.removeItem("redirectAfterLogin");
    token.value = null;
    user.value = null;
  };

  // Helper functions
  const hasPermission = (permission) => {
    return userPermissions.value.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    return permissionList.some((permission) =>
      userPermissions.value.includes(permission),
    );
  };

  const isAdmin = computed(() => user.value?.role === "admin");
  const isClient = computed(() => user.value?.role === "client");
  const isDeveloper = computed(() => user.value?.role === "developer");

  return {
    // State
    user,
    token,
    initialized,
    isAuthenticated,
    permissions: userPermissions,
    isAdmin,
    isClient,
    isDeveloper,

    // Actions
    initializeAuth,
    login,
    register,
    fetchUser,
    updateProfile,
    forgotPassword,
    resetPassword,
    logout,

    // Helpers
    hasPermission,
    hasAnyPermission,
    hasRole
  };
});
